<template>
  <div class="grid-with-scrollbar">
    <div ref="projectsGridRef" v-if="projects" class="project-grid" @scroll="onScroll">
      <div ref="cardContainerRef" class="project-card-container">
        <ProjectCard v-for="project in projects" :key="project._key" :project="project" @click="goToProject(project)" />
      </div>
    </div>

    <div v-show="enableScroll" ref="trackRef" class="custom-scrollbar-track" aria-hidden="true"
      @pointerdown="onTrackPointerDown">
      <div ref="thumbRef" class="custom-scrollbar-thumb" :style="{
        height: thumbHeightPx,
        transform: `translateY(${thumbTopPx})`
      }" @pointerdown.stop.prevent="onThumbPointerDown" />
    </div>
  </div>

  <div v-if="enableScroll" class="scroll-txt">scroll</div>
</template>

<style scoped>
.grid-with-scrollbar {
  position: relative;
  width: fit-content;
}

.project-grid {
  padding: 0 16px;
  max-height: 570px;
  width: fit-content;
  overflow-y: auto;
  /* Hide native scrollbars everywhere */
  scrollbar-width: none;
  -ms-overflow-style: none;
  will-change: transform;
  transform: translateZ(0);
}

.project-grid::-webkit-scrollbar {
  display: none;
}

.custom-scrollbar-track {
  position: absolute;
  top: 0px;
  bottom: 0px;
  right: 4px;
  width: 5px;
  border-radius: 999px;
  background: transparent;
  z-index: 1;
}

.custom-scrollbar-thumb {
  position: absolute;
  left: 0;
  right: 0;
  border-radius: 999px;
  background: #d9d9d9;
  will-change: transform;
  touch-action: none;
}

.project-card-container {
  display: flex;
  flex-wrap: wrap;
  overflow: hidden;
  width: 1350px;
  max-width: 100%;
  will-change: transform;
  transform: translateZ(0);
}

@media (max-width: 1560px) {
  .project-card-container {
    width: 1080px;
  }
}

@media (max-width: 1260px) {
  .project-card-container {
    width: calc(3 * 270px);
  }
}

@media (max-width: 996px) {
  .project-card-container {
    width: calc(3 * 270px + 6px);
    gap: 2px;
    padding: 0 0 250px 0;
  }

  .project-grid {
    max-height: calc(100vh - var(--navbar-height) - 33px);
  }

  .scroll-txt {
    display: none;
  }

  .custom-scrollbar-track {
    display: none;
  }
}

@media (max-width: 920px) {
  .project-card-container {
    width: calc(2 * 270px + 4px);
  }
}

@media (max-width: 648px) {
  .project-card-container {
    width: calc(1 * 270px + 2px);
  }
}

.scroll-txt {
  position: relative;
  top: v-bind(thumbTopPx);
  color: var(--primary-text-color);
  opacity: 0.3;
  width: fit-content;
  height: fit-content;
  margin-top: 6px;
}
</style>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import router from "@/router";
import ProjectCard from "../molecules/project-card.vue";

const props = defineProps({ projects: Array });

const projectsGridRef = ref(null);
const cardContainerRef = ref(null);
const trackRef = ref(null);
const thumbRef = ref(null);

const enableScroll = ref(false);

const trackHeight = ref(0);
const thumbHeight = ref(0);
const thumbTop = ref(0); // current top of thumb within track
const minThumb = 28;     // minimum thumb height for usability

const thumbHeightPx = computed(() => `${thumbHeight.value}px`);
const thumbTopPx = computed(() => `${thumbTop.value}px`);

let roGrid, roContent;  // ResizeObservers
let dragging = false;
let dragStartY = 0;
let dragStartThumbTop = 0;
let activePointerId = null;

function goToProject(project) {
  router.push(`/project/${project.slug}`);
}

function updateMetrics() {
  const grid = projectsGridRef.value;
  if (!grid) return;

  enableScroll.value = grid.scrollHeight > grid.clientHeight;
  if (!enableScroll.value) {
    thumbTop.value = 0;
    return;
  }

  trackHeight.value = Math.max(0, grid.clientHeight);

  const ratio = grid.clientHeight / grid.scrollHeight;
  thumbHeight.value = Math.max(minThumb, Math.floor(trackHeight.value * ratio));
  syncThumbToScroll();
}

function syncThumbToScroll() {
  const grid = projectsGridRef.value;
  if (!grid || !enableScroll.value) return;

  const maxScrollTop = grid.scrollHeight - grid.clientHeight;
  const maxThumbTop = Math.max(0, trackHeight.value - thumbHeight.value);

  const scrollRatio = maxScrollTop === 0 ? 0 : grid.scrollTop / maxScrollTop;
  thumbTop.value = Math.round(scrollRatio * maxThumbTop);
}

function syncScrollToThumb(newThumbTop) {
  const grid = projectsGridRef.value;
  if (!grid) return;

  const maxScrollTop = grid.scrollHeight - grid.clientHeight;
  const maxThumbTop = Math.max(0, trackHeight.value - thumbHeight.value);

  const clampedThumbTop = Math.min(Math.max(newThumbTop, 0), maxThumbTop);
  const ratio = maxThumbTop === 0 ? 0 : clampedThumbTop / maxThumbTop;
  grid.scrollTop = ratio * maxScrollTop;
  thumbTop.value = clampedThumbTop;
}

function onScroll() {
  syncThumbToScroll();
}

function onTrackPointerDown(e) {
  const track = trackRef.value;
  if (!track) return;
  const rect = track.getBoundingClientRect();
  const y = e.clientY - rect.top;
  syncScrollToThumb(y - thumbHeight.value / 2);
}

function onThumbPointerDown(e) {
  dragging = true;
  activePointerId = e.pointerId;
  dragStartY = e.clientY;
  dragStartThumbTop = thumbTop.value;
  try { thumbRef.value?.setPointerCapture?.(activePointerId); } catch { }
  window.addEventListener("pointermove", onPointerMove, { passive: true });
  window.addEventListener("pointerup", onPointerUp, { passive: true });
  document.body.style.userSelect = "none";
}

function onPointerMove(e) {
  if (!dragging || e.pointerId !== activePointerId) return;
  const delta = e.clientY - dragStartY;
  syncScrollToThumb(dragStartThumbTop + delta);
}

function onPointerUp(e) {
  if (e.pointerId !== activePointerId) return;
  dragging = false;
  activePointerId = null;
  window.removeEventListener("pointermove", onPointerMove);
  window.removeEventListener("pointerup", onPointerUp);
  document.body.style.userSelect = "";
}

function installObservers() {
  const grid = projectsGridRef.value;
  const content = cardContainerRef.value;

  if (grid) {
    roGrid = new ResizeObserver(updateMetrics);
    roGrid.observe(grid);
  }
  if (content) {
    roContent = new ResizeObserver(updateMetrics);
    roContent.observe(content);
  }

  window.addEventListener("resize", updateMetrics);
}

function removeObservers() {
  roGrid?.disconnect();
  roContent?.disconnect();
  window.removeEventListener("resize", updateMetrics);
}

onMounted(() => {
  installObservers();
  requestAnimationFrame(updateMetrics);
});

onBeforeUnmount(() => {
  removeObservers();
});

watch(
  () => props.projects,
  () => requestAnimationFrame(updateMetrics),
  { deep: true }
);
</script>
